import { type ReactNode, useEffect, useRef, useState } from 'react'
import { X, MoreHorizontal, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react'
import type { RequirementNode } from '@ispot/shared'

interface Props {
  node: RequirementNode
  position?: { index: number; total: number }
  onClose: () => void
  onPrev?: () => void
  onNext?: () => void
  onDelete?: () => void
}

export function DetailHeader({ node, position, onClose, onPrev, onNext, onDelete }: Props) {
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menuOpen) return
    function onPointer(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen])

  return (
    <div
      className="px-5 pt-3 pb-4 flex-shrink-0"
      style={{ borderBottom: '1px solid var(--color-border-subtle)' }}
    >
      <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
        {/* Prev / next navigation */}
        <div className="flex items-center gap-0.5">
          <IconButton onClick={onPrev} title="Previous (↑)" aria-label="Previous" disabled={!onPrev}>
            <ChevronLeft size={14} />
          </IconButton>
          <IconButton onClick={onNext} title="Next (↓)" aria-label="Next" disabled={!onNext}>
            <ChevronRight size={14} />
          </IconButton>
          {position && (
            <span
              style={{
                fontSize: 11.5,
                color: 'var(--color-fg-muted)',
                marginLeft: 4,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {position.index} of {position.total}
            </span>
          )}
        </div>

        {/* More / close */}
        <div className="flex items-center gap-0.5" ref={menuRef} style={{ position: 'relative' }}>
          {onDelete && (
            <IconButton title="More options" aria-label="More options" onClick={() => setMenuOpen(o => !o)}>
              <MoreHorizontal size={14} />
            </IconButton>
          )}
          {menuOpen && (
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% + 4px)',
                right: 0,
                zIndex: 50,
                minWidth: 140,
                background: 'var(--color-bg-card)',
                border: '1px solid var(--color-border)',
                borderRadius: 8,
                boxShadow: '0 8px 24px -8px rgba(29,26,21,0.18), 0 2px 6px -2px rgba(29,26,21,0.06)',
                padding: 4,
              }}
            >
              <button
                type="button"
                onClick={() => { setMenuOpen(false); onDelete?.() }}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer border-0 text-left text-sm"
                style={{ background: 'transparent', color: 'var(--color-danger)' }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(226,75,75,0.08)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <Trash2 size={13} />
                Delete requirement
              </button>
            </div>
          )}
          <IconButton onClick={onClose} title="Close (Esc)" aria-label="Close (Esc)">
            <X size={14} />
          </IconButton>
        </div>
      </div>

      <h2
        className="m-0 font-display"
        style={{
          fontSize: 22,
          fontWeight: 600,
          letterSpacing: '-0.01em',
          color: 'var(--color-fg)',
          lineHeight: 1.25,
        }}
      >
        {node.name}
      </h2>
    </div>
  )
}

interface IconButtonProps {
  children: ReactNode
  onClick?: () => void
  title: string
  'aria-label': string
  disabled?: boolean
}

function IconButton({ children, onClick, title, 'aria-label': ariaLabel, disabled }: IconButtonProps) {
  return (
    <button
      type="button"
      className="flex items-center justify-center rounded cursor-pointer border-0 bg-transparent"
      style={{
        width: 26,
        height: 26,
        color: disabled ? 'var(--color-fg-faint)' : 'var(--color-fg-muted)',
        transition: 'background 120ms',
        cursor: disabled ? 'default' : 'pointer',
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = 'var(--color-warm-200)' }}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
      onClick={disabled ? undefined : onClick}
      title={title}
      aria-label={ariaLabel}
      aria-disabled={disabled}
    >
      {children}
    </button>
  )
}
